// src/pages/GomelongRemoteControl.jsx
// ✅ Contrôle à distance GOMELONG (plateforme "Intelligent") — eau + électricité
// Backend : routers/gomelong_remote.py  →  /api/gomelong-remote
import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Search, Loader, Droplets, Zap, Power, PowerOff, RefreshCw, Send,
  History, Radio, Copy, X, Eye, Wifi, WifiOff, Activity, Receipt, UserCircle, Gauge, Save, Pencil, AlertTriangle,
} from 'lucide-react';
import { gomelongRemoteService } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';

// ── helpers ─────────────────────────────────────────────────────────────────
const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((e) => e.msg).join(' | ');
  return err?.message || 'Erreur inconnue';
};

// Erreur lisible + détail technique (pour le support)
const ErrorBox = ({ result, fallback = 'Aucune donnée' }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
    <p className="flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
      <span>{result?.error || fallback}</span>
    </p>
    {result?.technical && (
      <details className="mt-2 text-xs text-amber-700">
        <summary className="cursor-pointer select-none">Détails techniques (pour le support)</summary>
        <code className="block mt-1 break-all">{result.technical}</code>
      </details>
    )}
  </div>
);

// Ventes GOMELONG : le format exact de PageVend n'est pas documenté → affichage générique
const extractRows = (d) => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== 'object') return [];
  for (const k of ['Data', 'data', 'Items', 'items', 'List', 'list', 'Records', 'records', 'Rows', 'rows']) {
    if (Array.isArray(d[k])) return d[k];
    if (d[k] && typeof d[k] === 'object') { const inner = extractRows(d[k]); if (inner.length) return inner; }
  }
  return [];
};
const cellValue = (v) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

// 🇫🇷 Libellés des colonnes renvoyées par GOMELONG (ventes d'eau)
const PURCHASE_LABELS = {
  transactionid: 'N° transaction',
  watermeterid: 'Compteur',
  metercode: 'Compteur',
  purchasetoken: 'Token',
  token: 'Token',
  purchasedamount: 'Montant (FCFA)',
  amount: 'Montant (FCFA)',
  purchasevolume: 'Volume (m³)',
  volume: 'Volume (m³)',
  purchasedate: "Date d'achat",
  createtime: "Date d'achat",
  unitprice: 'Prix du m³',
  operatorid: 'Opérateur',
  customername: 'Client',
};
const labelOf = (k) => PURCHASE_LABELS[String(k).toLowerCase()] || k;
const fmtToken = (t) => String(t || '').replace(/\s/g, '').match(/.{1,4}/g)?.join(' ') || '—';
const fmtCell = (k, v) => {
  const key = String(k).toLowerCase();
  if (v === null || v === undefined || v === '') return '—';
  if (key.includes('date') || key.includes('time')) {
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString('fr-FR');
  }
  if (key.includes('token')) return fmtToken(v);
  if (key.includes('amount') || key.includes('price')) {
    const n = Number(v);
    return isNaN(n) ? String(v) : new Intl.NumberFormat('fr-FR').format(n);
  }
  if (key.includes('volume')) {
    const n = Number(v);
    return isNaN(n) ? String(v) : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 3 }).format(n)}`;
  }
  return cellValue(v);
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const isOnlineVal = (v) => v === 1 || v === true || v === '1';

const TASK_LABELS = { 0: 'En attente', 1: 'En cours', 2: 'Réussie', 3: 'Échouée', 4: 'Expirée' }; // ⚠️ à confirmer avec GOMELONG

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tones[tone]}`}>{children}</span>;
};

const Stat = ({ label, value, sub }) => (
  <div className="bg-slate-50 rounded-xl p-3">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-base font-bold text-slate-900 break-all">{value ?? '—'}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const ActionButton = ({ icon: Icon, label, onClick, tone = 'blue', disabled, loading }) => {
  const tones = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-emerald-600 hover:bg-emerald-700',
    red: 'bg-red-600 hover:bg-red-700',
    amber: 'bg-amber-500 hover:bg-amber-600',
    slate: 'bg-slate-600 hover:bg-slate-700',
    cyan: 'bg-cyan-600 hover:bg-cyan-700',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${tones[tone]} text-white rounded-xl px-4 py-3 font-semibold text-sm flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
};

// ── page ────────────────────────────────────────────────────────────────────
const GomelongRemoteControl = () => {
  const [meterInput, setMeterInput] = useState('');
  const [meterId, setMeterId] = useState('');
  const [info, setInfo] = useState(null);           // { local }
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [remoteState, setRemoteState] = useState({ loading: false, remote: null, collector: null });
  const [collectorEdit, setCollectorEdit] = useState(null); // null = lecture, string = édition
  const [busy, setBusy] = useState('');          // clé de l'action en cours
  const [history, setHistory] = useState([]);
  const [tokenInput, setTokenInput] = useState('');
  const [readDay, setReadDay] = useState(today());
  const [readings, setReadings] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [purchaseRange, setPurchaseRange] = useState({ start: daysAgo(30), end: today() });
  const [task, setTask] = useState(null);         // { id, status, data, polling }
  const [resultToken, setResultToken] = useState(null);
  const [detail, setDetail] = useState(null); // { title, rows: [[label, value]], token }
  const [confirm, setConfirm] = useState({ isOpen: false });
  const pollRef = useRef(null);

  const isWater = info?.local?.type === 'water';
  const unit = isWater ? 'm³' : 'kWh';

  useEffect(() => () => clearInterval(pollRef.current), []);

  // ── chargement ──
  // 1) fiche locale (immédiate)  2) état GOMELONG en arrière-plan (peut être lent)
  const loadRemote = async (id) => {
    setRemoteState((r) => ({ ...r, loading: true }));
    try {
      const res = await gomelongRemoteService.getMeterRemote(id);
      setRemoteState({ loading: false, remote: res.data?.remote, collector: res.data?.collector });
    } catch (err) {
      setRemoteState({
        loading: false,
        remote: { success: false, error: errMsg(err), technical: err?.response?.data?.detail?.technical },
        collector: null,
      });
    }
  };

  const loadMeter = async (id = meterInput.trim(), { reset = true } = {}) => {
    if (!id) return toast.warn('Saisissez un numéro de compteur');
    setLoadingInfo(true);
    if (reset) {
      setInfo(null);
      setReadings(null);
      setPurchases(null);
      setTask(null);
      setCollectorEdit(null);
      setRemoteState({ loading: false, remote: null, collector: null });
      clearInterval(pollRef.current);
    }
    try {
      const res = await gomelongRemoteService.getMeter(id);
      setInfo(res.data);
      setMeterId(id);
      loadHistory(id);
      if (res.data?.remote_configured) loadRemote(id); // pas de await : la fiche s'affiche tout de suite
    } catch (err) {
      toast.error(errMsg(err));
      if (reset) setMeterId('');
    } finally {
      setLoadingInfo(false);
    }
  };

  // Rafraîchit la fiche après une commande sans tout effacer
  const refreshMeter = () => loadMeter(meterId, { reset: false });

  const saveCollector = async () => {
    setBusy('collector');
    try {
      await gomelongRemoteService.setCollector(meterId, (collectorEdit || '').trim());
      toast.success(isWater ? 'Collecteur enregistré' : 'Concentrateur enregistré');
      setCollectorEdit(null);
      refreshMeter();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy('');
    }
  };

  const loadHistory = async (id = meterId) => {
    if (!id) return;
    try {
      const res = await gomelongRemoteService.getHistory(id);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
  };

  // ── exécution générique ──
  const run = async (key, fn, { successMsg, onSuccess } = {}) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(successMsg || 'Opération envoyée');
      onSuccess?.(res.data);
      loadHistory();
      return res.data;
    } catch (err) {
      toast.error(errMsg(err));
      loadHistory();
      return null;
    } finally {
      setBusy('');
      setConfirm({ isOpen: false });
    }
  };

  const ask = (title, message, onConfirm, type = 'danger') =>
    setConfirm({ isOpen: true, title, message, type, onConfirm });

  // ── suivi des tâches (électricité) ──
  const trackTask = (taskId) => {
    if (!taskId) return;
    clearInterval(pollRef.current);
    setTask({ id: taskId, status: null, data: null, polling: true });
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const res = await gomelongRemoteService.getTask(taskId);
        const status = res.data?.status?.data?.TaskStatus;
        const data = res.data?.data?.data;
        const done = [2, 3, 4].includes(status) || (Array.isArray(data) && data.length > 0);
        setTask({ id: taskId, status, data, polling: !done && tries < 20 });
        if (done || tries >= 20) clearInterval(pollRef.current);
      } catch {
        if (tries >= 20) clearInterval(pollRef.current);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  // ── actions EAU ──
  const waterValve = (action) =>
    ask(
      action === 'close' ? '🚰 Fermer la vanne' : '🚰 Ouvrir la vanne',
      `${action === 'close' ? "Couper l'eau" : "Rétablir l'eau"} du compteur ${meterId} ?`,
      () => run(`valve-${action}`, () => gomelongRemoteService.waterValve(meterId, action), {
        successMsg: action === 'close' ? 'Commande de fermeture envoyée' : "Commande d'ouverture envoyée",
        onSuccess: () => refreshMeter(),
      }),
      action === 'close' ? 'danger' : 'warning'
    );

  const loadReadings = () =>
    run('readings', () => gomelongRemoteService.waterData(meterId, readDay), {
      successMsg: 'Relevés chargés',
      onSuccess: (d) => setReadings(Array.isArray(d?.data) ? d.data : []),
    });

  const loadPurchases = () =>
    run('purchases', () => gomelongRemoteService.waterPurchases(meterId, {
      start_date: purchaseRange.start, end_date: purchaseRange.end, page_size: 50,
    }), {
      successMsg: 'Achats GOMELONG chargés',
      onSuccess: (d) => setPurchases(extractRows(d?.data)),
    });

  // ── actions ÉLEC ──
  const elecRelay = (action) =>
    ask(
      action === 'off' ? '🔌 Couper le courant' : '🔌 Rétablir le courant',
      `${action === 'off' ? 'Couper' : 'Rétablir'} le courant du compteur ${meterId} ?`,
      () => run(`relay-${action}`, () => gomelongRemoteService.elecRelay(meterId, action), {
        successMsg: 'Tâche envoyée au concentrateur',
        onSuccess: (d) => { trackTask(d?.task_id); refreshMeter(); },
      }),
      action === 'off' ? 'danger' : 'warning'
    );

  const elecRead = () =>
    run('read', () => gomelongRemoteService.elecRead(meterId), {
      successMsg: 'Lecture lancée',
      onSuccess: (d) => trackTask(d?.task_id),
    });

  // ── commun ──
  const sendToken = () => {
    const token = tokenInput.replace(/\s/g, '');
    if (token.length < 10) return toast.warn('Token invalide');
    const fn = isWater
      ? () => gomelongRemoteService.waterSendToken(meterId, token)
      : () => gomelongRemoteService.elecSendToken(meterId, token);
    run('send-token', fn, {
      successMsg: 'Token envoyé au compteur',
      onSuccess: (d) => { setTokenInput(''); if (!isWater) trackTask(d?.task_id); },
    });
  };

  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Copié'));

  // ── données distantes formatées ──
  const remote = remoteState.remote;
  const remoteData = remote?.data;
  const latestReading = isWater && Array.isArray(remoteData) && remoteData.length
    ? remoteData[remoteData.length - 1] : null;
  const elecDetail = !isWater && remoteData && !Array.isArray(remoteData) ? remoteData : null;
  const collectorResult = remoteState.collector;
  const collector = collectorResult?.data;

  return (
    <div className="min-h-screen p-4 lg:p-6 bg-gradient-to-br from-slate-50 to-cyan-50">
      <ToastContainer position="top-right" />
      <ConfirmModal
        isOpen={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        loading={!!busy}
        onClose={() => setConfirm({ isOpen: false })}
        onConfirm={confirm.onConfirm}
      />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-cyan-700 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Radio className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Contrôle à distance GOMELONG</h1>
              <p className="text-cyan-100 text-sm mt-1">Compteurs à eau et électriques</p>
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Numéro de compteur GOMELONG</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                value={meterInput}
                onChange={(e) => setMeterInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadMeter()}
                placeholder="Ex : 331150037162"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl font-mono text-lg focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <button
              onClick={() => loadMeter()}
              disabled={loadingInfo}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loadingInfo ? <Loader className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Charger
            </button>
          </div>
        </div>

        {info && (
          <>
            {/* Fiche compteur */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isWater ? 'bg-cyan-100' : 'bg-amber-100'}`}>
                      {isWater ? <Droplets className="w-6 h-6 text-cyan-700" /> : <Zap className="w-6 h-6 text-amber-600" />}
                    </div>
                    <div>
                      <p className="font-mono text-xl font-bold text-slate-900">{info.local.meter_id}</p>
                      <p className="text-sm text-slate-500">{isWater ? "Compteur à eau" : 'Compteur électrique'} · {info.local.brand}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Pill tone={info.local.status === 'active' ? 'green' : 'red'}>{info.local.status}</Pill>
                    {info.local.relay_state && (
                      <Pill tone={info.local.relay_state === 'connected' ? 'green' : 'red'}>
                        {isWater ? 'Vanne' : 'Relais'} : {info.local.relay_state === 'connected' ? 'ouvert(e)' : 'coupé(e)'}
                      </Pill>
                    )}
                  </div>
                </div>

                {info.local.customer && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 bg-blue-50 rounded-xl p-3">
                    <UserCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold">{info.local.customer.full_name}</span>
                    <span className="text-slate-500">· {info.local.customer.phone}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Tarif" value={`${info.local.tariff} F/${unit}`} />
                  <Stat
                    label="Cumul acheté"
                    value={`${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: isWater ? 3 : 2 }).format(Number(info.local.cumulative_purchased || 0))} ${unit}`}
                    sub={info.local.purchase_count != null
                      ? `${new Intl.NumberFormat('fr-FR').format(info.local.cumulative_amount || 0)} FCFA · ${info.local.purchase_count} achat${info.local.purchase_count > 1 ? 's' : ''}`
                      : undefined}
                  />
                  <Stat label="Collecteur" value={info.local.collector_id || 'Aucun'} />
                  <Stat label="Dernière opération" value={fmtDate(info.local.last_operation)} />
                </div>

                {/* État côté GOMELONG */}
                {info.remote_configured && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Gauge className="w-4 h-4" /> État remonté par GOMELONG
                    {!remoteState.loading && (
                      <button onClick={() => loadRemote(meterId)} className="ml-auto text-xs text-cyan-700 hover:underline flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> Actualiser
                      </button>
                    )}
                  </p>
                  {remoteState.loading ? (
                    <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-500 flex items-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" /> Interrogation de la plateforme GOMELONG…
                    </div>
                  ) : !remote ? null : !remote.success ? (
                    <ErrorBox result={remote} />
                  ) : isWater ? (
                    latestReading ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Stat label="Index" value={latestReading.CurrentReading} />
                        <Stat label="Volume restant" value={`${latestReading.SurplusWaterVolume ?? '—'} m³`} />
                        <Stat label="Conso. totale" value={`${latestReading.TotalWaterConsumption ?? '—'} m³`} />
                        <Stat label="Vanne" value={latestReading.ValveStatus} />
                        <Stat label="Dernière recharge" value={latestReading.LastRechargeVolume} />
                        <Stat label="Tension" value={latestReading.Voltage} />
                        <Stat label="Signal" value={latestReading.ReceivedSignalStrengthIndicator} />
                        <Stat label="Heure compteur" value={fmtDate(latestReading.WaterMeterTime)} />
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Aucun relevé aujourd'hui ni hier.</p>
                    )
                  ) : elecDetail ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Stat label="En ligne" value={isOnlineVal(elecDetail.IsOnline) ? 'Oui' : 'Non'} />
                      <Stat label="Crédit restant" value={elecDetail.RemainingAmount} />
                      <Stat label="Énergie totale" value={`${elecDetail.TotalEnergy ?? '—'} kWh`} />
                      <Stat label="État" value={elecDetail.State} />
                      <Stat label="Concentrateur" value={elecDetail.ConId} />
                      <Stat label="Mode prépayé" value={elecDetail.PrepayMode} />
                      <Stat label="Phase" value={elecDetail.MeterPhase} />
                      <Stat label="Dernière com." value={fmtDate(elecDetail.LastCommTime)} />
                    </div>
                  ) : null}
                </div>
                )}
              </div>

              {/* Collecteur */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Radio className="w-4 h-4" /> {isWater ? 'Collecteur' : 'Concentrateur'}
                </p>
                {/* ID du collecteur / concentrateur — modifiable directement ici */}
                {collectorEdit === null ? (
                  <div className="flex items-center justify-between gap-2 bg-slate-50 rounded-xl p-3">
                    <div>
                      <p className="text-xs text-slate-500">ID {isWater ? 'collecteur' : 'concentrateur'}</p>
                      <p className="font-mono font-bold text-slate-900">{info.local.collector_id || 'Non renseigné'}</p>
                    </div>
                    <button
                      onClick={() => setCollectorEdit(info.local.collector_id || '')}
                      className="px-3 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 rounded-lg flex items-center gap-1"
                    >
                      <Pencil className="w-4 h-4" /> {info.local.collector_id ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      autoFocus
                      value={collectorEdit}
                      onChange={(e) => setCollectorEdit(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && saveCollector()}
                      placeholder={isWater ? 'ID du collecteur' : 'ID du concentrateur'}
                      className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl font-mono focus:border-cyan-500 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveCollector}
                        disabled={busy === 'collector'}
                        className="flex-1 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                      >
                        {busy === 'collector' ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
                      </button>
                      <button onClick={() => setCollectorEdit(null)} className="px-3 py-2 bg-slate-100 rounded-lg text-sm font-semibold">
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {info.remote_configured && info.local.collector_id && (
                  remoteState.loading ? (
                    <p className="text-sm text-slate-500 flex items-center gap-2"><Loader className="w-4 h-4 animate-spin" /> Vérification de l'état…</p>
                  ) : collector ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        {isOnlineVal(collector.IsOnline)
                          ? <><Wifi className="w-5 h-5 text-emerald-600" /><Pill tone="green">En ligne</Pill></>
                          : <><WifiOff className="w-5 h-5 text-red-600" /><Pill tone="red">Hors ligne</Pill></>}
                      </div>
                      <Stat label="Dernière communication" value={fmtDate(collector.LastCommTime)} />
                      <Stat label="Adresse" value={collector.InstallAddress} />
                    </div>
                  ) : collectorResult ? (
                    <ErrorBox result={collectorResult} fallback="État indisponible" />
                  ) : null
                )}
              </div>
            </div>

            {/* Actions (visibles uniquement si le contrôle à distance est actif) */}
            {info.remote_configured && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-5">
              <h2 className="text-lg font-bold text-slate-900">Commandes à distance</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {isWater ? (
                  <>
                    <ActionButton icon={Power} label="Ouvrir la vanne" tone="green" onClick={() => waterValve('open')} loading={busy === 'valve-open'} />
                    <ActionButton icon={PowerOff} label="Fermer la vanne" tone="red" onClick={() => waterValve('close')} loading={busy === 'valve-close'} />
                  </>
                ) : (
                  <>
                    <ActionButton icon={Power} label="Rétablir courant" tone="green" onClick={() => elecRelay('on')} loading={busy === 'relay-on'} />
                    <ActionButton icon={PowerOff} label="Couper courant" tone="red" onClick={() => elecRelay('off')} loading={busy === 'relay-off'} />
                    <ActionButton icon={Activity} label="Lecture à distance" tone="blue" onClick={elecRead} loading={busy === 'read'} />
                    <ActionButton icon={RefreshCw} label="Rafraîchir" tone="slate" onClick={refreshMeter} loading={loadingInfo || remoteState.loading} />
                  </>
                )}
              </div>

              {/* Envoi de token */}
              <div className="border-t border-slate-100 pt-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Envoyer un token au compteur (sans saisie par le client)
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder="20 chiffres"
                    className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl font-mono tracking-widest focus:border-cyan-500 focus:outline-none"
                  />
                  <ActionButton icon={Send} label="Envoyer" tone="cyan" onClick={sendToken} loading={busy === 'send-token'} />
                </div>
              </div>

              {/* Suivi de tâche (élec) */}
              {task && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-blue-900 flex items-center gap-2">
                      {task.polling && <Loader className="w-4 h-4 animate-spin" />}
                      Tâche #{task.id} — {TASK_LABELS[task.status] ?? (task.status ?? 'en attente…')}
                    </p>
                    <button onClick={() => { clearInterval(pollRef.current); setTask(null); }} className="text-blue-400 hover:text-blue-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {Array.isArray(task.data) && task.data.length > 0 && (
                    <table className="w-full mt-3 text-sm">
                      <thead><tr className="text-left text-blue-700"><th className="py-1">Paramètre</th><th>Valeur</th><th>Heure</th></tr></thead>
                      <tbody>
                        {task.data.map((r, i) => (
                          <tr key={i} className="border-t border-blue-100">
                            <td className="py-1 font-mono">{r.ParamId}</td>
                            <td className="font-semibold">{r.DataVal}</td>
                            <td className="text-slate-500">{r.MeterTime}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Relevés + achats (eau) */}
            {isWater && (
              <div className={`grid grid-cols-1 ${info.remote_configured ? 'lg:grid-cols-2' : ''} gap-6`}>
                {info.remote_configured && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2"><Gauge className="w-4 h-4" /> Relevés du jour</h3>
                    <div className="flex gap-2">
                      <input type="date" value={readDay} onChange={(e) => setReadDay(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
                      <button onClick={loadReadings} className="px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm flex items-center gap-1">
                        {busy === 'readings' ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Charger
                      </button>
                    </div>
                  </div>
                  {readings === null ? (
                    <p className="text-sm text-slate-400">Choisissez une date puis cliquez sur Charger.</p>
                  ) : readings.length === 0 ? (
                    <p className="text-sm text-slate-500">Aucun relevé pour cette date.</p>
                  ) : (
                    <div className="overflow-x-auto max-h-80">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-left text-slate-600">
                            <th className="p-2">Heure</th><th className="p-2">Index</th><th className="p-2">Restant</th><th className="p-2">Vanne</th><th className="p-2">Tension</th>
                          </tr>
                        </thead>
                        <tbody>
                          {readings.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="p-2 whitespace-nowrap">{fmtDate(r.WaterMeterTime)}</td>
                              <td className="p-2 font-mono">{r.CurrentReading}</td>
                              <td className="p-2">{r.SurplusWaterVolume}</td>
                              <td className="p-2">{r.ValveStatus}</td>
                              <td className="p-2">{r.Voltage}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4" /> Achats GOMELONG</h3>
                    <div className="flex gap-2 flex-wrap">
                      <input type="date" value={purchaseRange.start} onChange={(e) => setPurchaseRange({ ...purchaseRange, start: e.target.value })} className="px-2 py-2 border rounded-lg text-sm" />
                      <input type="date" value={purchaseRange.end} onChange={(e) => setPurchaseRange({ ...purchaseRange, end: e.target.value })} className="px-2 py-2 border rounded-lg text-sm" />
                      <button onClick={loadPurchases} className="px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm flex items-center gap-1">
                        {busy === 'purchases' ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Charger
                      </button>
                    </div>
                  </div>
                  {purchases === null ? (
                    <p className="text-sm text-slate-400">Choisissez une période puis cliquez sur Charger.</p>
                  ) : purchases.length === 0 ? (
                    <p className="text-sm text-slate-500">Aucun achat sur la période.</p>
                  ) : (
                    <div className="overflow-x-auto max-h-80">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-left text-slate-600">
                            {Object.keys(purchases[0] || {}).map((k) => <th key={k} className="p-2 whitespace-nowrap">{labelOf(k)}</th>)}
                            <th className="p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.map((p, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              {Object.keys(purchases[0] || {}).map((k) => (
                                <td key={k} className="p-2 whitespace-nowrap font-mono text-xs">{fmtCell(k, p[k])}</td>
                              ))}
                              <td className="p-2">
                                <button
                                  onClick={() => setDetail({
                                    title: 'Achat GOMELONG',
                                    rows: Object.keys(p).map((k) => [labelOf(k), fmtCell(k, p[k])]),
                                    token: Object.keys(p).find((k) => k.toLowerCase().includes('token')) ? p[Object.keys(p).find((k) => k.toLowerCase().includes('token'))] : null,
                                  })}
                                  className="px-2 py-1 text-xs font-semibold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg flex items-center gap-1"
                                >
                                  <Eye className="w-3.5 h-3.5" /> Voir
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Historique */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2"><History className="w-4 h-4" /> Historique des opérations</h3>
                <button onClick={() => loadHistory()} className="text-sm text-cyan-700 hover:underline flex items-center gap-1">
                  <RefreshCw className="w-4 h-4" /> Actualiser
                </button>
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-slate-400">Aucune opération pour ce compteur.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-600">
                        <th className="p-2">Date</th><th className="p-2">Opération</th><th className="p-2">Opérateur</th><th className="p-2">Statut</th><th className="p-2">Détail</th><th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-t border-slate-100">
                          <td className="p-2 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                          <td className="p-2 font-medium">{h.label}</td>
                          <td className="p-2">{h.username}</td>
                          <td className="p-2">
                            <Pill tone={h.status === 'success' ? 'green' : 'red'}>{h.status === 'success' ? 'Envoyée' : 'Échec'}</Pill>
                          </td>
                          <td className={`p-2 text-xs max-w-md ${h.status === 'success' ? 'text-slate-500' : 'text-red-700'}`} title={h.error_message || h.reason || h.token || ''}>
                            {h.error_message || h.token || h.reason || '—'}
                          </td>
                          <td className="p-2">
                            <button
                              onClick={() => setDetail({
                                title: h.label,
                                rows: [
                                  ['Date', fmtDate(h.created_at)],
                                  ['Opération', h.label],
                                  ['Opérateur', h.username || '—'],
                                  ['Statut', h.status === 'success' ? 'Envoyée' : 'Échec'],
                                  ['Motif / info', h.reason || '—'],
                                  ...(h.error_message ? [['Erreur', h.error_message]] : []),
                                ],
                                token: h.token || null,
                              })}
                              className="px-2 py-1 text-xs font-semibold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg flex items-center gap-1"
                            >
                              <Eye className="w-3.5 h-3.5" /> Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modale détail (bouton Voir) */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{detail.title}</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {detail.token && (
              <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-4 text-center font-mono text-lg font-bold tracking-widest break-all text-cyan-900 mb-4">
                {fmtToken(detail.token)}
              </div>
            )}
            <dl className="divide-y divide-slate-100 text-sm">
              {detail.rows.map(([label, value], i) => (
                <div key={i} className="flex justify-between gap-4 py-2">
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="font-medium text-slate-900 text-right break-all">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="flex gap-3 mt-5">
              {detail.token && (
                <button onClick={() => copy(String(detail.token).replace(/\s/g, ''))} className="flex-1 bg-cyan-600 text-white rounded-xl py-2.5 font-semibold flex items-center justify-center gap-2">
                  <Copy className="w-4 h-4" /> Copier le token
                </button>
              )}
              <button onClick={() => setDetail(null)} className="flex-1 bg-slate-100 rounded-xl py-2.5 font-semibold">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale token résultat */}
      {resultToken && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{resultToken.title}</h3>
              <button onClick={() => setResultToken(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-4 text-center font-mono text-xl font-bold tracking-widest break-all text-cyan-900">
              {resultToken.token.replace(/\s/g, '').match(/.{1,4}/g)?.join(' ')}
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => copy(resultToken.token)} className="flex-1 bg-cyan-600 text-white rounded-xl py-2.5 font-semibold flex items-center justify-center gap-2">
                <Copy className="w-4 h-4" /> Copier
              </button>
              <button onClick={() => setResultToken(null)} className="flex-1 bg-slate-100 rounded-xl py-2.5 font-semibold">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GomelongRemoteControl;